import i18n from "../i18n";
import React, { useState, useEffect } from "react";
import {
  Group,
  Text,
  Badge,
  CopyButton,
  Tooltip,
  ActionIcon,
  Button,
} from "@mantine/core";
import { useServerApi } from "../hooks/userServerApi";
import { useCellRender } from "../hooks/useCellRender";
import {
  IconPhoto,
  IconBuildingSkyscraper,
  IconSearch,
  IconLink,
  IconCopy,
  IconCheck,
  IconEye,
  IconEyeOff,
} from "@tabler/icons";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import DataTable from "./dataTable";
import { FormSectionCard } from "../layout/page";
import moment from "moment";
import { ImageThumbList } from "./clientPaymentList";
import ReactJson from "react-json-view";
import { useAuthUser } from "react-auth-kit";

const PortalLoginTab = ({ provider }) => {
  const [api] = useServerApi();
  const [portalLogin, setPortalLogin] = useState([]);
  const [revealedPasswords, setRevealedPasswords] = useState({});

  const auth = useAuthUser();

  const getPasswordKey = (data) =>
    data._id ||
    `${data.label || "login"}-${data.username || ""}-${data.url || ""}`;

  const togglePasswordVisibility = (key) => {
    setRevealedPasswords((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const fetchPortalLogin = async () => {
    if (!provider?._id) return;
    const data = await api.Provider.getPublicLogin(provider._id);
    if (data) setPortalLogin(data);

    if (auth().id) {
      const personalLogin = await api.User.getPortalLogin(
        auth().id,
        provider._id
      );
      if (_.isEmpty(personalLogin)) return;
      setPortalLogin([...data, ...personalLogin]);
    }

    // const personalLogin = await api.Provider.getUserLogin(provider._id);
    // console.log("Fetch portal login", data);
  };

  useEffect(() => {
    fetchPortalLogin();
  }, [provider]);

  const portalLoginColumns = [
    {
      headerName: "Label",
      field: "label",
      cellRender: (column, data) => (
        <Group>
          <Badge color={data.isPublic ? "green" : "blue"} radius={0} size="xs">
            {data.isPublic ? "Public" : "Private"}
          </Badge>
          <Text>{data.label}</Text>
        </Group>
      ),
    },
    {
      headerName: "URL",
      field: "url",
      cellRender: (column, data) => (
        <a
          href={`${data.url}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          {_.truncate(data.url, { length: 40 })}
        </a>
      ),
    },
    {
      headerName: "Login Name",
      field: "username",
      cellRender: (column, data) => (
        <Group noWrap spacing="xs" justify="space-between" w="100%">
          <Text>{data.username ? data.username : "N/A"}</Text>
          <CopyButton value={data.username} timeout={2000}>
            {({ copied, copy }) => (
              <Tooltip
                label={copied ? "Copied" : "Copy"}
                withArrow
                position="right"
                size="xs"
              >
                <ActionIcon
                  color={copied ? "teal" : "gray"}
                  variant="subtle"
                  onClick={copy}
                >
                  {copied ? (
                    <IconCheck style={{ width: 16 }} />
                  ) : (
                    <IconCopy style={{ width: 16 }} />
                  )}
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
        </Group>
      ),
    },
    {
      headerName: "Password",
      field: "password",
      cellRender: (column, data) => {
        const passwordKey = getPasswordKey(data);
        const isRevealed = !!revealedPasswords[passwordKey];
        const password = data.password || "";

        return (
          <Group noWrap spacing="xs" justify="space-between" w="100%">
            <Text>
              {!password
                ? "N/A"
                : isRevealed
                  ? password
                  : "•".repeat(Math.min(password.length, 12))}
            </Text>
            {password && (
              <>
                <Tooltip
                  label={isRevealed ? "Hide password" : "Show password"}
                  withArrow
                  position="right"
                  size="xs"
                >
                  <ActionIcon
                    color="gray"
                    variant="subtle"
                    onClick={() => togglePasswordVisibility(passwordKey)}
                  >
                    {isRevealed ? (
                      <IconEyeOff style={{ width: 16 }} />
                    ) : (
                      <IconEye style={{ width: 16 }} />
                    )}
                  </ActionIcon>
                </Tooltip>
                <CopyButton value={password} timeout={2000}>
                  {({ copied, copy }) => (
                    <Tooltip
                      label={copied ? "Copied" : "Copy"}
                      withArrow
                      position="right"
                      size="xs"
                    >
                      <ActionIcon
                        color={copied ? "teal" : "gray"}
                        variant="subtle"
                        onClick={copy}
                      >
                        {copied ? (
                          <IconCheck style={{ width: 16 }} />
                        ) : (
                          <IconCopy style={{ width: 16 }} />
                        )}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </>
            )}
          </Group>
        );
      },
    },
  ];

  return (
    <>
      {/* <ReactJson src={auth()} style={{ background: "white" }} collapsed /> */}

      {portalLogin.length === 0 && (
        <FormSectionCard mt="xl" ml="xl">
          <Text size="xs">{i18n.t("No Portal Login found")}</Text>
        </FormSectionCard>
      )}

      {portalLogin.length > 0 && (
        <>
          <FormSectionCard mt="xl" ml="xl">
            <DataTable
              data={portalLogin}
              columns={portalLoginColumns}
              hidePagination={true}
              hideSearchBar={true}
              showHeader={false}
              padding={0}
            />
          </FormSectionCard>
        </>
      )}
    </>
  );
};

export default PortalLoginTab;
